import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon, CalendarClock, DollarSign, FileText, Trophy, Plus } from 'lucide-react';
import { contractService } from '../prospect/_shared/api';
import type { ContractStats } from '../prospect/_shared/types';

/**
 * Dashboard do perfil VENDEDOR (Fase 26e) — espelha o nível vendedor do
 * app prospect: minhas vendas de hoje/mês, ticket médio, total e top planos.
 */
export default function VendedorDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ContractStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contractService
      .myStats()
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  const cards = [
    {
      label: 'Lançados hoje',
      value: stats?.launchedToday,
      icon: <CalendarIcon size={20} />,
      accent: 'text-primary bg-primary-soft/30',
    },
    {
      label: 'Lançados no mês',
      value: stats?.launchedThisMonth,
      icon: <CalendarClock size={20} />,
      accent: 'text-info bg-info-soft/30',
    },
    {
      label: 'Ticket médio',
      value:
        stats !== null
          ? stats.averageTicket.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          : undefined,
      icon: <DollarSign size={20} />,
      accent: 'text-success bg-success-soft/30',
    },
    {
      label: 'Total de contratos',
      value: stats?.totalContracts,
      icon: <FileText size={20} />,
      accent: 'text-warning bg-warning-soft/30',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="bg-surface border border-line rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted uppercase tracking-wider">{c.label}</span>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.accent}`}>
                {c.icon}
              </div>
            </div>
            <div className="text-2xl font-bold text-fg">
              {loading ? '…' : (c.value ?? '—')}
            </div>
          </div>
        ))}
      </div>

      {stats && stats.topPlans.length > 0 && (
        <div className="bg-surface border border-line rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={18} className="text-warning" />
            <h2 className="text-lg font-semibold text-fg">Meus planos mais vendidos</h2>
          </div>
          <div className="space-y-2">
            {stats.topPlans.map((p, i) => (
              <div
                key={p.erpId}
                className="flex items-center justify-between rounded-lg bg-canvas border border-line px-4 py-2.5"
              >
                <span className="text-sm text-fg">
                  <span className="text-muted mr-2">{i + 1}º</span>
                  {p.descricao}
                </span>
                <span className="text-sm font-bold text-primary">{p.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate('/prospect/launched')}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-solid hover:bg-primary-solid-hover text-white text-sm font-medium transition"
      >
        <Plus size={16} />
        Meus contratos
      </button>
    </div>
  );
}
